import { useRef, type CSSProperties, type ElementType, type ReactNode } from "react";
import { useInView } from "../hooks/useInView";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

type RevealProps = {
  children: ReactNode;
  /** Element HTML do wyrenderowania (domyślnie div) - pozwala owinąć np. <section> bez dodatkowego diva. */
  as?: ElementType;
  /** Opóźnienie w ms - do ręcznego stopniowania, jeśli nie używasz RevealGroup. */
  delayMs?: number;
  /** Kierunek wjazdu. "up" pasuje do nagłówków, "none" do samego fade. */
  direction?: "up" | "none";
  /** Dodatkowy blur startowy - subtelny efekt "wyostrzania się" treści. */
  withBlur?: boolean;
  className?: string;
  style?: CSSProperties;
};

/**
 * Owija dowolny fragment istniejącej sekcji (nagłówek, kartę, blok tekstu)
 * i animuje go raz, gdy wejdzie w viewport. Nie zmienia struktury ani treści
 * dziecka - tylko dodaje transform/opacity/transition na wrapperze.
 *
 * Użycie:
 *   <Reveal as="h2">Stworzeni do wymagających technologii...</Reveal>
 *   <Reveal delayMs={80}><AdvantageCard .../></Reveal>
 */
export function Reveal({
  children,
  as: Tag = "div",
  delayMs = 0,
  direction = "up",
  withBlur = false,
  className,
  style,
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref as React.RefObject<Element>, { once: true, margin: "0px 0px -12% 0px" });
  const reducedMotion = usePrefersReducedMotion();

  // With reduced motion, content is never hidden — no opacity/transform at all.
  const hidden = !inView && !reducedMotion;

  const transitionStyle: CSSProperties = {
    transitionProperty: "opacity, transform, filter",
    transitionDuration: reducedMotion ? "200ms" : "var(--pr-dur, 700ms)",
    transitionTimingFunction: "var(--pr-ease, cubic-bezier(0.16, 1, 0.3, 1))", // expo-out
    transitionDelay: reducedMotion ? "0ms" : `${delayMs}ms`,
  };

  const revealStyle: CSSProperties = reducedMotion
    ? {}
    : {
        opacity: hidden ? 0 : 1,
        transform: hidden && direction === "up" ? "translateY(16px)" : "translateY(0)",
        filter: withBlur ? (hidden ? "blur(4px)" : "blur(0px)") : undefined,
        willChange: "opacity, transform",
      };

  return (
    <Tag ref={ref} className={className} style={{ ...transitionStyle, ...revealStyle, ...style }}>
      {children}
    </Tag>
  );
}

type RevealGroupProps = {
  children: ReactNode[];
  /** Odstęp między kolejnymi dziećmi w ms - kaskada 70-90ms wygląda najlepiej. */
  staggerMs?: number;
  className?: string;
  itemClassName?: string;
  direction?: "up" | "none";
};

/**
 * Do gridów: 4 karty advantage, 6 zasad, karty opinii.
 * Owija każde dziecko w Reveal z narastającym opóźnieniem, bez zmiany
 * układu grida (przekaż tu bezpośrednio elementy karty, grid zostaje na rodzicu).
 */
export function RevealGroup({ children, staggerMs = 80, className, itemClassName, direction = "up" }: RevealGroupProps) {
  return (
    <>
      {children.map((child, i) => (
        <Reveal key={i} delayMs={i * staggerMs} className={className ?? itemClassName} direction={direction}>
          {child}
        </Reveal>
      ))}
    </>
  );
}

type RevealLinesProps = {
  /** Linie nagłówka - każda dostaje własną maskę. Pojedynczy string = jedna linia. */
  lines: string[];
  /** Tag do wyrenderowania (domyślnie h2). */
  as?: ElementType;
  /** Odstęp kaskady między liniami w ms (znak rozpoznawczy edytorskich stron). */
  staggerMs?: number;
  /** Globalne opóźnienie startu. */
  startDelayMs?: number;
  className?: string;
  /** Tekst tylko dla czytników ekranu, doklejony wewnątrz nagłówka (SEO/AX). */
  srOnly?: string;
};

/**
 * Line-mask reveal: każda linia nagłówka wyjeżdża z dołu spod overflow:hidden,
 * z przesunięciem staggerMs. Subtelny, edytorski efekt topowych stron.
 *
 * paddingBottom + ujemny marginBottom kompensują clip "ogonków" (g, y, ą, ę)
 * przy ciasnym line-height nagłówków - bez zmiany layoutu.
 * Przy prefers-reduced-motion renderuje czysty tekst, natychmiast widoczny.
 */
export function RevealLines({ lines, as: Tag = "h2", staggerMs = 80, startDelayMs = 0, className, srOnly }: RevealLinesProps) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref as React.RefObject<Element>, { once: true, margin: "0px 0px -12% 0px" });
  const reducedMotion = usePrefersReducedMotion();
  // Always gate on IO - reducedMotion just skips the stagger delay and shortens duration
  const show = inView;
  const visible = lines.filter((l) => l != null && String(l).trim() !== "");

  return (
    <Tag ref={ref} className={className}>
      {visible.map((line, i) => (
        <span
          key={i}
          style={{
            display: "block",
            overflow: "hidden",
            paddingBottom: "0.12em",
            marginBottom: "-0.12em",
          }}
        >
          <span
            style={{
              display: "block",
              transform: show ? "translateY(0)" : "translateY(110%)",
              transitionProperty: "transform",
              transitionDuration: reducedMotion ? "200ms" : "var(--pr-dur, 700ms)",
              transitionTimingFunction: "var(--pr-ease, cubic-bezier(0.16, 1, 0.3, 1))",
              transitionDelay: reducedMotion ? "0ms" : `${startDelayMs + i * staggerMs}ms`,
            }}
          >
            {line}
          </span>
        </span>
      ))}
      {srOnly ? <span className="sr-only">{srOnly}</span> : null}
    </Tag>
  );
}
